import { useState, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Link as MuiLink,
} from "@mui/material";
import {
  applyConsent,
  readConsent,
  saveConsent,
  isConsentExpired,
  isGpcEnabled,
  DEFAULT_COOKIE_PREFERENCES,
  OPEN_COOKIE_SETTINGS_EVENT,
  type CookiePreferences,
} from "../utils/cookie-consent";

export function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  // Диалог можно закрыть без выбора только когда он переоткрыт из настроек
  // (при первом визите выбор обязателен — согласие ещё не дано).
  const [dismissable, setDismissable] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(
    DEFAULT_COOKIE_PREFERENCES,
  );
  // GPC-сигнал браузера (CPRA opt-out от sale/sharing) — отражаем в UI и блокируем
  // маркетинговую категорию; фактический enforcement — в applyConsent (SSOT).
  const gpcActive = isGpcEnabled();

  // Восстановление ранее данного согласия (или показ баннера при его отсутствии).
  useEffect(() => {
    const saved = readConsent();
    if (!saved) {
      setShowBanner(true);
    } else {
      setPreferences(saved);
      // Текущее согласие продолжает применяться до нового выбора.
      applyConsent(saved);
      // R12: если согласие устарело (> TTL) — переспросить, показав баннер повторно.
      if (isConsentExpired()) {
        setShowBanner(true);
      }
    }
  }, []);

  // Переоткрытие настроек cookie из любого места (кнопка «Настройки cookie»).
  useEffect(() => {
    const handleOpen = () => {
      setPreferences(readConsent() ?? DEFAULT_COOKIE_PREFERENCES);
      setShowSettings(true);
      setDismissable(true);
      setShowBanner(true);
    };
    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, handleOpen);
    return () =>
      window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, handleOpen);
  }, []);

  const acceptAll = () => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
    };
    savePreferences(allAccepted);
  };

  const acceptNecessary = () => {
    const necessaryOnly: CookiePreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false,
    };
    savePreferences(necessaryOnly);
  };

  const savePreferences = (prefs: CookiePreferences) => {
    saveConsent(prefs);
    setPreferences(prefs);
    applyConsent(prefs); // синхронизация с gtag/fbq: и grant, и revoke
    setShowBanner(false);
    setShowSettings(false);
    setDismissable(false);
  };

  if (!showBanner) return null;

  return (
    <Dialog
      open={showBanner}
      // Первый визит — закрытие запрещено (нужен явный выбор); переоткрытие из
      // настроек — можно закрыть без изменений (согласие уже сохранено ранее).
      onClose={() => {
        if (dismissable) {
          setShowBanner(false);
          setShowSettings(false);
        }
      }}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          m: 0,
          borderRadius: "8px 8px 0 0",
        },
      }}
    >
      <DialogTitle>
        <Typography component="span" variant="h6" display="block">
          Мы используем cookies
        </Typography>
      </DialogTitle>
      <DialogContent>
        {!showSettings ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Мы используем cookies для улучшения вашего опыта, аналитики и
              маркетинга. Вы можете принять все cookies или настроить их
              использование. Подробнее в нашей{" "}
              <MuiLink href="/privacy-policy" underline="hover">
                Политике конфиденциальности
              </MuiLink>
              .
            </Typography>

            <Box display="flex" gap={2} flexWrap="wrap">
              <Button variant="outlined" onClick={acceptNecessary}>
                Только необходимые
              </Button>
              <Button variant="outlined" onClick={() => setShowSettings(true)}>
                Настроить
              </Button>
              <Button variant="contained" onClick={acceptAll}>
                Принять все
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Настройки cookies
            </Typography>

            {gpcActive && (
              <Alert severity="info" sx={{ mb: 3 }}>
                Обнаружен сигнал Global Privacy Control (GPC) вашего браузера.
                Маркетинговые cookies («продажа/передача» данных) отключены
                автоматически в соответствии с вашим сигналом отказа.
              </Alert>
            )}

            <Box sx={{ mb: 3 }}>
              <CookieOption
                label="Необходимые cookies"
                description="Требуются для базовой функциональности сайта"
                checked={true}
                disabled={true}
                onChange={() => {}}
              />
              <CookieOption
                label="Аналитические cookies"
                description="Помогают нам улучшать наш сервис"
                checked={preferences.analytics}
                onChange={(checked) =>
                  setPreferences({ ...preferences, analytics: checked })
                }
              />
              <CookieOption
                label="Маркетинговые cookies"
                description={
                  gpcActive
                    ? "Отключены сигналом Global Privacy Control (GPC)"
                    : "Используются для персонализации рекламы"
                }
                checked={gpcActive ? false : preferences.marketing}
                disabled={gpcActive}
                onChange={(checked) =>
                  setPreferences({ ...preferences, marketing: checked })
                }
              />
              <CookieOption
                label="Персонализация"
                description="Сохранение ваших предпочтений"
                checked={preferences.preferences}
                onChange={(checked) =>
                  setPreferences({ ...preferences, preferences: checked })
                }
              />
            </Box>

            <Box display="flex" gap={2}>
              <Button variant="outlined" onClick={() => setShowSettings(false)}>
                Назад
              </Button>
              <Button
                variant="contained"
                onClick={() => savePreferences(preferences)}
              >
                Сохранить настройки
              </Button>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CookieOption({
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <Box display="flex" alignItems="flex-start" gap={2} sx={{ mb: 2 }}>
      <Checkbox
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <Box>
        <Typography variant="body2" fontWeight="medium">
          {label}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {description}
        </Typography>
      </Box>
    </Box>
  );
}
